import {
  OFFERS_HUB_OFFER_TAGS,
  type OffersHubHeroCarouselItem,
  type OffersHubOffer,
  type OffersHubOfferTag,
} from './types';

const OFFER_CARD_TITLE_PREVIEW_LENGTH = 15;

function createAlphabeticDraftSuffix(index: number): string {
  const alphabetLength = 26;
  let value = index;
  let label = '';

  do {
    label = String.fromCharCode(65 + (value % alphabetLength)) + label;
    value = Math.floor(value / alphabetLength) - 1;
  } while (value >= 0);

  return label;
}

export function createHeroCarouselItemDraftLabel(index: number): string {
  return `Hero item ${createAlphabeticDraftSuffix(index)}`;
}

export function createOfferDraftLabel(index: number): string {
  return `Offer ${createAlphabeticDraftSuffix(index)}`;
}

function truncateCardPreview(value: string): string {
  return value.length > OFFER_CARD_TITLE_PREVIEW_LENGTH
    ? `${value.slice(0, OFFER_CARD_TITLE_PREVIEW_LENGTH)}...`
    : value;
}

function offerCardPreviewText(offer: OffersHubOffer): string {
  return offer.offerTag === 'partner_offer' ? offer.brandName : offer.title;
}

export function formatHeroItemCardTitle(item: OffersHubHeroCarouselItem): string {
  const label = item.draftLabel;
  const previewSource = item.title.trim();
  if (!previewSource) {
    return label;
  }

  return `${label} (${truncateCardPreview(previewSource)})`;
}

export function formatOfferCardTitle(offer: OffersHubOffer): string {
  const label = offer.draftLabel;
  const previewSource = offerCardPreviewText(offer).trim();
  if (!previewSource) {
    return label;
  }

  return `${label} (${truncateCardPreview(previewSource)})`;
}

export function getNextOfferDraftLabel(offers: Array<OffersHubOffer>): string {
  const usedLabels = new Set(offers.map((offer) => offer.draftLabel));
  let index = 0;
  let nextLabel = createOfferDraftLabel(index);

  while (usedLabels.has(nextLabel)) {
    index += 1;
    nextLabel = createOfferDraftLabel(index);
  }

  return nextLabel;
}

export function getNextHeroCarouselItemDraftLabel(items: Array<OffersHubHeroCarouselItem>): string {
  const usedLabels = new Set(items.map((item) => item.draftLabel));
  let index = 0;
  let nextLabel = createHeroCarouselItemDraftLabel(index);

  while (usedLabels.has(nextLabel)) {
    index += 1;
    nextLabel = createHeroCarouselItemDraftLabel(index);
  }

  return nextLabel;
}

export function offerSectionLabel(offerTag: OffersHubOfferTag): string {
  switch (offerTag) {
    case 'partner_offer':
      return 'Partner offer';
    case 'luxury_escapes_offer':
      return 'Luxury Escapes offer';
  }
}

export function getOffersByTag(offers: Array<OffersHubOffer>, offerTag: OffersHubOfferTag): Array<OffersHubOffer> {
  return offers.filter((offer) => offer.offerTag === offerTag);
}

export function groupOffersByTag(offers: Array<OffersHubOffer>): Array<OffersHubOffer> {
  return OFFERS_HUB_OFFER_TAGS.flatMap((offerTag) => getOffersByTag(offers, offerTag));
}

export function replaceOffersByTag(
  offers: Array<OffersHubOffer>,
  offerTag: OffersHubOfferTag,
  nextOffers: Array<OffersHubOffer>,
): Array<OffersHubOffer> {
  return groupOffersByTag(
    OFFERS_HUB_OFFER_TAGS.flatMap((currentOfferTag) =>
      currentOfferTag === offerTag ? nextOffers : getOffersByTag(offers, currentOfferTag),
    ),
  );
}

function moveItem<T>(items: Array<T>, index: number, offset: -1 | 1): Array<T> {
  const targetIndex = index + offset;
  if (targetIndex < 0 || targetIndex >= items.length) {
    return items;
  }

  const nextItems = [...items];
  [nextItems[index], nextItems[targetIndex]] = [nextItems[targetIndex], nextItems[index]];
  return nextItems;
}

export function moveOffer(offers: Array<OffersHubOffer>, index: number, offset: -1 | 1): Array<OffersHubOffer> {
  return moveItem(offers, index, offset);
}

export function moveHeroCarouselItem(
  items: Array<OffersHubHeroCarouselItem>,
  index: number,
  offset: -1 | 1,
): Array<OffersHubHeroCarouselItem> {
  return moveItem(items, index, offset);
}
